import { css } from '@emotion/react';
import styled from '@emotion/styled';
import Icon from '@mdi/react';
import { transparentize } from 'polished';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import AsyncCreatableSelect from 'react-select/async-creatable';
import CreatableSelect from 'react-select/creatable';
import { forPhoneOnly, Variables } from '../variables';

export const DropdownIndicator = styled.div`
    padding: 0 4px;
    display: flex;
    path {
        fill: ${Variables.Swatches.Gray7};
    }

    ${forPhoneOnly(`
        padding: 0 4px 0 0;
    `)};
`;

const successState = css`
    border-color: ${Variables.Colors.successful};
    &:not(:disabled) {
        &:hover,
        &:focus,
        &:active {
            border-color: ${Variables.Colors.successful};
        }
    }
`;

const errorState = css`
    border-color: ${Variables.Colors.warning};
    &:not(:disabled) {
        &:hover {
            border-color: ${Variables.Colors.warning};
        }

        &:focus,
        &:active {
            border-color: ${Variables.Colors.warning};
        }
    }
`;

export const SelectContainer = styled.div`
    display: flex;
    flex-direction: column;
    position: relative;
    width: 100%;
    white-space: pre-wrap;
`;

interface IBaseSelectProps {
    showSuccess: boolean;
    showError: boolean;
}

export const BaseSelect = (props: IBaseSelectProps) => css`
    color: ${Variables.Swatches.Gray7};

    .select {
        &__multi-value {
            background-color: unset;
            &__label {
                font-size: 14px;
                padding: 0;
            }

            &__remove {
                margin-left: 3px;
            }
        }

        &__control {
            border-radius: ${Variables.Rounded.small};
            border: 1px solid ${Variables.Swatches.Gray3};
            background-color: ${Variables.Swatches.White};
            min-height: 32px;
            color: ${Variables.Swatches.Gray8};
            cursor: pointer;

            &--is-focused {
                border: 2px solid ${Variables.Swatches.Blue4};
                box-shadow: none;
            }

            &--is-disabled {
                cursor: default;
                border-color: ${Variables.Colors.nx8};
                background-color: ${Variables.Colors.nx4};
                color: ${Variables.Colors.n15};
                & ${DropdownIndicator} {
                    path {
                        fill: ${Variables.Colors.n15};
                    }
                }
                .select__value-container {
                    &:not(.select__value-container--has-value) {
                        visibility: hidden;
                    }
                }
            }

            &:hover {
                border-color: ${Variables.Swatches.Blue4};
            }
        }

        &__value-container {
            font-size: 14px;
            margin: 4px;
            padding: 0 4px;
            overflow-y: auto;
        }

        &__single-value {
            top: 0;
            bottom: 0;
            margin: auto 2px;
            transform: none;
            display: flex;
            align-items: center;
        }

        &__placeholder {
            white-space: nowrap;
            color: ${Variables.Swatches.Gray5};
            font-size: 14px;
        }

        &__indicator-separator {
            display: none;
        }

        &__menu {
            overflow: hidden;
            box-shadow: 0px 6px 12px 0px ${Variables.Swatches.AlphaDarkGray10};
            border-radius: ${Variables.Rounded.xsmall};
            &-list {
                padding: 2px;
            }
        }

        &__option {
            cursor: pointer;
            padding: 4px;
            border-radius: ${Variables.Rounded.xsmall};
            min-height: 24px;
            font-size: 14px;
            display: flex;
            align-items: center;
            
            &--is-focused {
                background-color: ${Variables.Colors.n15_24};
            }

            &--is-selected {
                color: ${Variables.Colors.b300};
                font-weight: 500;
                background-color: inherit;
                &:hover {
                    background-color: ${Variables.Colors.n15_24};
                }
            }

            &:active {
                background-color: ${Variables.Colors.n15_24};
            }
        }
    }

    ${forPhoneOnly(`
        .select__value-container {
            margin: 0 0 0 4px;
        }
    `)};
`;

export const StyledSelect: any = styled(Select)`
    ${BaseSelect}
`;

export const StyledCreatableSelect: any = styled(CreatableSelect)`
    ${BaseSelect}
`;

export const StyledAsyncCreatableSelect: any = styled(AsyncCreatableSelect)`
    ${BaseSelect}
`;

export const StyledAsyncSelect: any = styled(AsyncSelect)`
    ${BaseSelect}
`;

export const SelectBottom = styled.div`
    font-size: 13px;
    font-weight: 500;
    display: flex;
    align-items: center;
    margin-top: 8px;
`;

export const SelectBottomSuccess = styled(SelectBottom)`
    color: ${Variables.Colors.successful};
    fill: ${Variables.Colors.successful};
`;

export const SelectBottomError = styled(SelectBottom)`
    color: ${Variables.Colors.warning};
    fill: ${Variables.Colors.warning};
`;

export const SelectBottomIcon = styled(Icon)`
    width: 18px;
    height: 18px;
    vertical-align: middle;
`;

export const SelectBottomMessage = styled.div`
    display: inline-block;
    margin-left: 4px;
`;

export const MenuWrapper = styled.div<IBaseSelectProps>`
    ${BaseSelect}
`;
